import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactTooltip from 'react-tooltip';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import Button from '@components/common/button/Button';
import EmergencyContact from '@components/ssap/pages/pageIncludes/common/emergencyContact/EmergencyContact';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import PageHeader from '../headers/PageHeader';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import HomeAddressInputs from './pageIncludes/common/address/HomeAddressInputs';
import MailingAddressInputs from './pageIncludes/common/address/MailingAddressInputs';
import NamesInputs from './pageIncludes/common/names/NamesInputs';

// Hooks

import { usePrevious } from '@hooks/usePrevious';

// ActionCreators / Selectors

import { showModal } from '@actions/common/actionCreators';
import { fetchCommunicationPreferences } from '@actions/individualPortal/preferences/communicationPreferencesActionCreators';
import { changeData, fetchCountiesForZipcode, setCounty, validateAddress } from '@actions/ssap/ssapActionCreators';
import { selectCommunicationPreferences, selectIfLivingArrangement } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { compareStringsCaseInsensitively } from '@utils/miscellaneous/compareStrings';
import { normalizeBooleanToString } from '@utils/normalize/normalizeBooleanToString';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import { preventerTools } from '@utils/tools/preventerTools';
import validators from '@utils/validators/validators';

// Constants

import EXTERNAL_LINKS from '@constants/configs/externalEndpoints';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { PHONE_TYPES, SSAP_JSON_PROP_NAMES, USPS_GENERAL_DELIVERY_ADDRESS } from '@constants/ssap/miscellaneous/miscellaneous';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';

// Types

import { INPUT_TYPES as BUTTON_INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import { ContactMethod } from '@components/ssap/SsapTypes';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';

/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Primary Contact Information' Page
 *
 */
const Page1_02  = props => {

    const {
        counties,
        data,
        data: {
            taxHousehold: {
                householdMember: householdMembers
            }
        },
        environment: {
            exchangeName,
            flags: { emailChangeAllowed },
            localDates: { serverDate },
            links: { PRIVACY_POLICY },
            stateCode,
            isState,
            user: { userActiveRole }
        },
        form
    } = props;

    const {
        phoneVerified,
        smsVerified,
        smsUnsubscribed,
    } = useSelector(selectCommunicationPreferences);
    const livingArrangementEnabled = useSelector(selectIfLivingArrangement);

    const isIndividualUser = userActiveRole.toUpperCase() === ALL_USER_ROLES.INDIVIDUAL;

    const {
        addressDefaults: {
            defaultMailingAddressId
        },
        formName,
        itfFeatureEnabled,
        pageName
    } = props;

    const {
        isFormSubmitted,
        isFormOnTopLevelValid
    } = preventerTools.provideObject(form[formName]);

    const primaryContact = householdMemberTools.getPrimaryContact(householdMembers);

    const {
        dateOfBirth,
        emergencyContactInfo: {
            name: {
                firstName: emergencyContactFirstName,
                lastName: emergencyContactLastName
            }
        },
        livingArrangement = 'UNSPECIFIED',
        householdContact,
        householdContact: {
            homeAddress,
            homeAddress: {
                postalCode,
                county,
                countyCode,
                primaryAddressCountyFipsCode,
                streetAddress1,
                streetAddress2,
                state,
                city,
            },
            phone: {
                phoneNumber,
            },
            otherPhone: {
                phoneNumber: _phoneNumber,
                phoneExtension
            },
            contactPreferences: {
                emailAddress,
                emailMe,
                paperless1095,
                preferredContactMethod,
                preferredSpokenLanguage,
                preferredWrittenLanguage,
                textMe
            },
            mailingAddressSameAsHomeAddressIndicator,
            mailingAddress,
            mailingAddress: {
                postalCode: _postalCode,
                countyCode: _countyCode,
                streetAddress1: _streetAddress1,
                streetAddress2: _streetAddress2,
                state: _state,
                city: _city,
            },
            outOfStateAttestation
        },
        name: {
            firstName,
            middleName,
            lastName,
            suffix
        },
        noHomeAddressIndicator
    } = primaryContact;

    const intl = useIntl();

    const [ localState, setLocalState ] = useState({
        addressBeforeChanges: {
            postalCode: null,
            streetAddress1: null,
            state: null,
            city: null
        }
    });

    const [ isBlur, toggleBlur ] = useState(false);

    useEffect(() => {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
    }, []);

    // **********************

    const HOME_ADDRESS = 'HOME_ADDRESS';
    const MAILING_ADDRESS = 'MAILING_ADDRESS';

    const checkPostalCode = postalCode => {
        return validators
            .find(validator => validator.validatorType === VALIDATION_TYPES.ZIP_CODE)
            .verifier({ value: postalCode });
    };

    const previousHomePostalCode = usePrevious(postalCode);
    const previousMailingPostalCode = usePrevious(_postalCode);

    const fetchCountiesForAddress = (state, postalCode, addressType) => {
        if(state && checkPostalCode(postalCode)) {
            props.actions.fetchCountiesForZipcode(state, postalCode.slice(0, 5), addressType);
        }
    };

    useEffect(() => {
        fetchCountiesForAddress(state, postalCode, HOME_ADDRESS);
    }, [ `${state}${postalCode}` ]);

    useEffect(() => {
        !mailingAddressSameAsHomeAddressIndicator && fetchCountiesForAddress(_state, _postalCode, MAILING_ADDRESS);
    }, [ `${_state}${_postalCode}`, mailingAddressSameAsHomeAddressIndicator ]);
    
    const fetchedCounties = addressType => (cc => cc ? cc : [])(counties[addressType]);
    
    const setTheOnlyOneCounty = (addressType) => {

        const address = addressType === HOME_ADDRESS ?
            SSAP_JSON_PROP_NAMES.HOME_ADDRESS :
            SSAP_JSON_PROP_NAMES.MAILING_ADDRESS;

        if(fetchedCounties(addressType).length === 1) {
            const { countyCode } = fetchedCounties(addressType)
                .reduce((res, c) => {
                    const [ countyCode ] = Object.entries(c)[0];
                    return {
                        ...res,
                        countyCode
                    };
                }, {});

            let newState = { ...data };
            newState
                .taxHousehold
                .householdMember = householdMemberTools.updatePrimaryContact(householdMembers, {
                    householdContact: {
                        ...primaryContact.householdContact,
                        [address]: {
                            ...primaryContact.householdContact[address],
                            countyCode
                        }
                    }
                });
            props.actions.changeData(newState);
        }
    };
    
    useEffect(() => {
        setTheOnlyOneCounty(HOME_ADDRESS);
    }, [ fetchedCounties(HOME_ADDRESS).map(c => Object.keys(c)).toString() ]);

    useEffect(() => {
        setTheOnlyOneCounty(MAILING_ADDRESS);
    }, [ fetchedCounties(MAILING_ADDRESS).map(c => Object.keys(c)).toString() ]);
    
    const cleanUpCountyCode = (postalCode, addressType) => {

        const address = addressType === HOME_ADDRESS ?
            SSAP_JSON_PROP_NAMES.HOME_ADDRESS :
            SSAP_JSON_PROP_NAMES.MAILING_ADDRESS;

        const previousCode = addressType === HOME_ADDRESS ? previousHomePostalCode : previousMailingPostalCode;

        if(previousCode && previousCode !== postalCode) {
            fetchedCounties(addressType).length > 0 && props.actions.setCounty([], addressType);
            let newState = { ...data };
            newState
                .taxHousehold
                .householdMember = householdMemberTools.updatePrimaryContact(householdMembers, {
                    householdContact: {
                        ...primaryContact.householdContact,
                        [address]: {
                            ...primaryContact.householdContact[address],
                            countyCode: null
                        }
                    }
                });
            props.actions.changeData(newState);
        }
    };

    useEffect(() => {
        cleanUpCountyCode(postalCode, HOME_ADDRESS);
    }, [ postalCode ]);

    useEffect(() => {
        !mailingAddressSameAsHomeAddressIndicator && cleanUpCountyCode(_postalCode, MAILING_ADDRESS);
    }, [ _postalCode ]);

    // **********************

    useEffect(() => {

        if(checkPostalCode(postalCode)) {
            const newState = { ...data };

            let primaryContact = householdMemberTools.getPrimaryContact(newState.taxHousehold.householdMember);

            primaryContact = {
                ...primaryContact,
                householdContact: {
                    ...primaryContact.householdContact,
                    mailingAddress: primaryContact.householdContact.homeAddress
                }
            };

            newState.taxHousehold.otherAddresses[0] = primaryContact.householdContact.homeAddress;

            if(mailingAddressSameAsHomeAddressIndicator) {
                newState
                    .taxHousehold
                    .householdMember = householdMemberTools.updatePrimaryContact(
                        newState.taxHousehold.householdMember, 
                        { ...primaryContact }
                    );
            }

            props.actions.changeData(newState);
        }
    }, Object.values(homeAddress));

    // ******* County - County Code - Primary Address County Fips Code syncing ***** START ******
    
    const syncUpCountyRelatedProps = addressType => {

        const address = addressType === HOME_ADDRESS ?
            SSAP_JSON_PROP_NAMES.HOME_ADDRESS :
            SSAP_JSON_PROP_NAMES.MAILING_ADDRESS;

        const newState = { ...data };

        const [ updFips, updCounty ] =
            (c => c ? Object.entries(c)[0] : [ primaryAddressCountyFipsCode, county ] )(fetchedCounties(addressType).find(c => c[householdContact[address].countyCode]));

        const updatedAddress = {
            ...primaryContact.householdContact[address],
            county: updCounty,
            primaryAddressCountyFipsCode: updFips,
        };

        newState
            .taxHousehold
            .householdMember = householdMemberTools.updatePrimaryContact(householdMembers,
                {
                    householdContact: {
                        ...primaryContact.householdContact,
                        [address]: updatedAddress
                    }
                });

        props.actions.changeData(newState);
    };
    
    useEffect(() => {
        if(homeAddress.countyCode && homeAddress.countyCode !== homeAddress.primaryAddressCountyFipsCode) {
            syncUpCountyRelatedProps(HOME_ADDRESS);
        }
    }, [ homeAddress.countyCode ]);

    useEffect(() => {
        if(mailingAddress.countyCode && mailingAddress.countyCode !== mailingAddress.primaryAddressCountyFipsCode && !mailingAddressSameAsHomeAddressIndicator) {
            syncUpCountyRelatedProps(MAILING_ADDRESS);
        }
    }, [ mailingAddress.countyCode ]);

    // ******* County - County Code - Primary Address County Fips Code syncing ***** END ******


    // Here we on focus inputs related to Address (streetAddress1, city, postalCode, state) make a snapshot
    // to know if after User's updates in inputs to invoke async address validation or not

    const snapshotAddress = () => {

        toggleBlur(false);

        const {
            postalCode,
            streetAddress1,
            state,
            city,
        } = primaryContact.householdContact.homeAddress;

        setLocalState({
            addressBeforeChanges: {
                postalCode,
                streetAddress1,
                state,
                city,
            }
        });
    };

    // Updating some props in JSON with the simultaneous cleaning up county and county code

    const onFieldChange = (...args) => {

        const newState = cloneObject(data);

        const [ newData, , fields, [ name, value, , text ] ] = args;
        let syncOtherMembers = [];

        if(name === SSAP_JSON_PROP_NAMES.NO_HOME_ADDRESS) {
            if (value) {
                newData.householdContact.mailingAddressSameAsHomeAddressIndicator = false;
                newData.householdContact.homeAddress.streetAddress1 = USPS_GENERAL_DELIVERY_ADDRESS;
                newData.householdContact.homeAddress.streetAddress2 = null;
            } else {
                newData.householdContact.homeAddress.streetAddress1 = null;
            }
        }

        if(
            name === SSAP_JSON_PROP_NAMES.STREET_ADDRESS_1
            && value
            && compareStringsCaseInsensitively(value, USPS_GENERAL_DELIVERY_ADDRESS)
        ) {
            newData.householdContact.homeAddress.streetAddress1 = null;
        }

        if(name === SSAP_JSON_PROP_NAMES.STATE && fields[1] === SSAP_JSON_PROP_NAMES.HOME_ADDRESS && value === stateCode) {
            newData.householdContact.outOfStateAttestation = false;
        }

        if(
            name === SSAP_JSON_PROP_NAMES.EMAIL_ADDRESS
            && !fields.some(f => f === SSAP_JSON_PROP_NAMES.EMERGENCY_CONTACT_INFO)
            && !value
        ) {
            newData.householdContact.contactPreferences.emailMe = false;
        }

        if(name === SSAP_JSON_PROP_NAMES.EMAIL_ME && !newData.householdContact.contactPreferences.emailAddress) {
            return null;
        }

        if(name === SSAP_JSON_PROP_NAMES.TEXT_ME && !newData.householdContact.phone.phoneNumber) {
            return null;
        }

        if(name === SSAP_JSON_PROP_NAMES.COUNTY_CODE) {
            newData.householdContact[fields[1]].county = text;
            newData.householdContact[fields[1]].primaryAddressCountyFipsCode = value;
        }

        if (name === SSAP_JSON_PROP_NAMES.LIVING_ARRANGEMENT) {
            newData.livingArrangement = value;
            syncOtherMembers = [...syncOtherMembers, { syncKey: [name], checkKey: ['livesAtOtherAddressIndicator'], checkValues: [false, null, undefined] }];
        }

        const currentState_01 = newData.householdContact.homeAddress.state;

        if (currentState_01 !== primaryContact.householdContact.homeAddress.state) {
            newData.householdContact.homeAddress.county = null;
            newData.householdContact.homeAddress.countyCode = null;
        }

        const currentState_02 = newData.householdContact.mailingAddress.state;

        if (currentState_02 !== primaryContact.householdContact.mailingAddress.state) {
            newData.householdContact.mailingAddress.county = null;
            newData.householdContact.mailingAddress.countyCode = null;
        }

        newState.taxHousehold.householdMember = householdMemberTools.updatePrimaryContact(newState.taxHousehold.householdMember, { ...newData }, false, syncOtherMembers);
        props.actions.changeData(newState);
    };

    const handleContactUpdate = (newData, name, value) => {
        const newState = cloneObject(data);

        let updatedPrimaryContactPerson;

        switch(name) {
            case SSAP_JSON_PROP_NAMES.EMAIL_ADDRESS: {
                newData.householdContact.contactPreferences.emailAddress = value;

                if (!value) {
                    newData.householdContact.contactPreferences.emailMe = false;
                }

                updatedPrimaryContactPerson = newData;
            }
                break;

            case SSAP_JSON_PROP_NAMES.PHONE_NUMBER: {
                const pcp = householdMemberTools.getPrimaryContact(newState.taxHousehold.householdMember);
                pcp.householdContact.phone.phoneNumber = value;

                updatedPrimaryContactPerson = pcp;
            }
                break;

            default:
                return;
        }

        newState.taxHousehold.householdMember = householdMemberTools.updatePrimaryContact(newState.taxHousehold.householdMember, updatedPrimaryContactPerson);
        props.actions.changeData(newState);
    };

    const handleChangeEmailModal = () => {
        props.actions.showModal({
            modalType: dashboardModalTypes.CHANGE_EMAIL_MODAL,
            modalData: {
                data: primaryContact,
                name: 'emailAddress',
                value: emailAddress,
                type: INPUT_TYPES.REGULAR_INPUT,
                validationType: VALIDATION_TYPES.EMAIL,
                updateData: handleContactUpdate,
            },
        });
    };

    const handleChangePhoneModal = () => {
        props.actions.showModal({
            modalType: dashboardModalTypes.CHANGE_PHONE_MODAL,
            modalData: {
                data: primaryContact.householdContact.phone,
                name: 'phoneNumber',
                value: phoneNumber,
                type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
                callbackOnClose: () => props.actions.fetchCommunicationPreferences(null, true)
            },
        });
    };

    const handleDeleteMobilePhoneNumber = () => {
        const newState = cloneObject(data);
        const pcp = householdMemberTools.getPrimaryContact(newState.taxHousehold.householdMember);
        pcp.householdContact.phone.phoneNumber = null;
        pcp.householdContact.contactPreferences.textMe = false;

        newState.taxHousehold.householdMember = householdMemberTools
            .updatePrimaryContact(
                newState.taxHousehold.householdMember,
                pcp
            );

        props.actions.changeData(newState);
    };

    // Hide/show mailing address and:
    // a) if checked to overwrite <mailingAddress> object to be equal to <homeAddress> object
    // b) if unchecked to clean up <mailingAddress> object

    const onCheckTheSameAddress = newData => {

        const newState = { ...data };

        const sameAddress = newData
            .householdContact
            .mailingAddressSameAsHomeAddressIndicator ?

            { ...primaryContact.householdContact.homeAddress } :
            { ...cloneObject(EMPTY_OBJECTS.HOUSEHOLD_CONTACT.mailingAddress) };

        newState.taxHousehold.householdMember = householdMemberTools.updatePrimaryContact(householdMembers, {
            ...newData,
            householdContact: {
                ...newData.householdContact,
                mailingAddress: sameAddress
            }
        });
        
        props.actions.changeData(newState);
    };

    // Async validation of home address
    // If exact match: nothing on UI
    // If we have discrepancy - show modal where User decides what to do: to agree with suggestions or not


    const {
        homeAddress: currentHomeAddress,
    } = primaryContact.householdContact;

    const { addressBeforeChanges }  = localState;

    const isAddressCanBeValidated = (() => {
        let result = true;

        if(streetAddress1 === USPS_GENERAL_DELIVERY_ADDRESS) {
            return false;
        }

        [ streetAddress1, city, state, postalCode ].forEach(value => {
            if (value === '' || value === null) {
                result = false;
            }
        });
        return result;
    })();

    const isAddressesTheSame = (() => {
        let result = true;
        Object.keys(addressBeforeChanges).forEach(key => {
            if (addressBeforeChanges[key] !== currentHomeAddress[key]) {
                result = false;
            }
        });
        return result;
    })();

    useEffect(() => {
        if(!isAddressesTheSame && isAddressCanBeValidated && isBlur) {
            props.actions.validateAddress(
                null,
                {
                    addressLine1: streetAddress1,
                    addressLine2: streetAddress2,
                    city: city,
                    state: state,
                    zipcode: postalCode
                },
                stateCode
            );
        }
    }, [ normalizeBooleanToString(!isAddressesTheSame, isAddressCanBeValidated, isBlur) ]);

    useEffect(() => {
        if(itfFeatureEnabled) {
            const newState = { ...data };

            newState.taxHousehold.otherAddresses =
                newState.taxHousehold.otherAddresses
                    .reduce((addresses, address) => {
                        const { addressId } = address;
                        return addressId === defaultMailingAddressId
                            ? [
                                ...addresses,
                                {
                                    ...mailingAddress,
                                    addressId: defaultMailingAddressId,
                                    deletable: false
                                }
                            ]
                            : [ ...addresses, address ] ;
                    }, []);

            props.actions.changeData(newState);
        }
    }, [Object.values(mailingAddress).toString()]);

    const validateAddress = () => {
        toggleBlur(true);
    };

    const handlePhoneChange = (...args) => {

        const [ newData , , [ , phoneType ], [ , value ] ] = args;

        const newState = { ...data };

        if(phoneType === SSAP_JSON_PROP_NAMES.PHONE && !value) {
            newData.householdContact.contactPreferences.textMe = false;
        }

        const type = phoneType === SSAP_JSON_PROP_NAMES.PHONE ? PHONE_TYPES.MOBILE : PHONE_TYPES.HOME;

        newState
            .taxHousehold
            .householdMember = householdMemberTools.updatePrimaryContact(householdMembers, {
                householdContact: {
                    ...primaryContact.householdContact,
                    [phoneType]: {
                        ...primaryContact.householdContact[phoneType],
                        phoneNumber: newData.householdContact[phoneType].phoneNumber,
                        phoneType: type
                    }
                }
            });

        props.actions.changeData(newState);
    };

    const onlyOneNameChunkIsProvided = useMemo(() => {
        return [emergencyContactFirstName, emergencyContactLastName].filter(v => !!v).length === 1;
    }, [ emergencyContactFirstName, emergencyContactLastName ]);

    const isNameWarningShown = useMemo(() => {
        return !!(isFormSubmitted && !isFormOnTopLevelValid && onlyOneNameChunkIsProvided);
    }, [ onlyOneNameChunkIsProvided, isFormSubmitted, isFormOnTopLevelValid ]);

    const contactMethodComplete = useMemo(() => {
        if(preferredContactMethod === ContactMethod.MAIL && !paperless1095) {
            return true;
        } else {
            return !!(emailMe || textMe);
        }
    }, [ preferredContactMethod, emailMe, textMe ]);

    const checkTopLevelValidity = () => {
        switch(true) {
            case isState.VA && onlyOneNameChunkIsProvided:
                return false;
            default:
                return contactMethodComplete;
        }
    };

    const generalDeliveryLink = (
        <a
            className = 'usa-link--external'
            href = { EXTERNAL_LINKS.GENERAL.USPS_GENERAL_DELIVERY }
            target = '_blank'
            rel = 'noopener noreferrer'
        >
            <FormattedMessage id = 'ssap.pcp.general_delivery_link_anchor' />
        </a>
    );

    return (
        <Fragment>
            <div id = 'saPrimaryContact' >
                <Form
                    { ...{
                        formName,
                        pageName,
                        topLevelValidity: checkTopLevelValidity()
                    } }
                >
                    <div className="subsection">
                        <PageHeader
                            text = 'ssap.subheader.primary_contact_name'
                            headerClassName = 'gi-heading-alt'
                        />
                        <NamesInputs
                            { ...{
                                firstName,
                                middleName,
                                lastName,
                                suffix,
                                currentData: primaryContact,
                                fields: [ 'name' ],
                                isRequired: true,
                                inputActions: {
                                    onFieldChange
                                },
                                underFormControl: true,
                                isInputStandalone: true
                            } }
                        />

                        <UniversalInput
                            label = { {
                                labelClass: [ 'tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label' ],
                                text: 'ssap.input.label.birth_date'
                            } }
                            inputData = { {
                                divClass: [ 'tablet:grid-col-6' ],
                                inputClass: [ 'gi-dropdown' ],
                                isInputRequired: true,
                                type: INPUT_TYPES.DATE_PICKER,
                                name: 'dateOfBirth',
                                value: dateOfBirth,
                                valuesToMatch: serverDate,
                                validationType: VALIDATION_TYPES.DATE_NOT_GREATER_THAN_CURRENT,
                                currentData: primaryContact,
                                fields: []
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                            indexInArray = { 0 }
                        />

                        <div className="grid-row gi-component gi-regular_input align-items-end">
                            <label
                                htmlFor='ssap.input.label.email'
                                className={`tablet:grid-col-4 gi-regular_input__label iconed-false required-${isIndividualUser}`}
                            >
                                <FormattedMessage id="ssap.input.label.email" />
                            </label>

                            <div className='tablet:grid-col-6' >
                                <UniversalInput
                                    errorClasses={['m-0', 'mb-4']}
                                    aria-labelledby={'ssap.input.label.email'}
                                    inputData={{
                                        isInputRequired: isIndividualUser,
                                        type: INPUT_TYPES.DUMMY,
                                        name: 'emailAddress',
                                        value: emailAddress,
                                        validationType: VALIDATION_TYPES.EMAIL,
                                        isInputStandalone: true,
                                        renderAs: (
                                            <div className={'grid-row justify-content-between'}>
                                                {emailAddress &&
                                                    <div className={'grid-col-8 text-left text-break margin-top-2'} data-test-id='preferences_email' >
                                                        {emailAddress}
                                                    </div>
                                                }
                                                <Button
                                                    id='preferences_email_button'
                                                    data-tip={!emailChangeAllowed ? intl.formatMessage({ id: 'ssap.tooltip.change_email_button' }) : null}
                                                    dataTestId='preferences_email_button'
                                                    name='emailAddress'
                                                    classes={['usa-button', 'gi-button__outline']}
                                                    type={BUTTON_INPUT_TYPES.BUTTON}
                                                    btnText={`ssap.button.${emailAddress ? 'change_email' : 'add_email'}`}
                                                    icon='edit'
                                                    iconPlacement='START'
                                                    handleClick={handleChangeEmailModal}
                                                    disabled={!emailChangeAllowed}
                                                    renderWithIconOnly={!!emailAddress}
                                                />
                                                <ReactTooltip effect="solid" place="top" className='tooltip-component' />
                                            </div>
                                        )
                                    }}
                                />
                            </div>
                        </div>

                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.email_me'
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'emailMe',
                                classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02' ],
                                checked: emailMe,
                                value: emailMe,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'contactPreferences' ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                        />
                    </div>

                    <div className = 'subsection'>
                        <PageHeader
                            text = 'ssap.subheader.primary_contact_home_address'
                            headerClassName = 'gi-heading-alt'
                        />

                        <UniversalInput
                            label = { {
                                dataInstructions: 'instructional_text.no_home_address',
                                instructionsViaPortal: [ true, '7rem' ],
                                labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                replacements: {
                                    exchangeName
                                },
                                isInputStandalone: true,
                                ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                text: 'ssap.input.label.no_home_address'
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'noHomeAddressIndicator',
                                classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02 text-no-wrap' ],
                                checked: noHomeAddressIndicator,
                                value: noHomeAddressIndicator,
                                currentData: primaryContact
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                        />

                        <HomeAddressInputs
                            { ...{
                                postalCode,
                                countyCode,
                                livingArrangement,
                                livingArrangementEnabled,
                                streetAddress1,
                                streetAddress2,
                                state,
                                city,
                                outOfStateAttestation,
                                currentData: cloneObject(primaryContact),
                                counties: fetchedCounties(HOME_ADDRESS),
                                inputActions: {
                                    onFieldChange,
                                    onFieldBlur: validateAddress,
                                    onFieldFocus: snapshotAddress
                                },
                                stateCode,
                                underFormControl: true,
                                isInputStandalone: true,
                                areStreetLinesVisible: !noHomeAddressIndicator
                            } }
                        />
                        
                    </div>

                    <div className = 'subsection' >
                        <PageHeader
                            text = 'ssap.subheader.primary_contact_mailing_address'
                            headerClassName = 'gi-heading-alt'
                        />

                        {!isState.ID && <AlertInfo
                            id = 'ssap.pcp.general_delivery_alert'
                            textReplacements = { { generalDeliveryLink } }
                        />}

                        <fieldset className = 'usa-fieldset' >
                            <div className = 'usa-checkbox' >
                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.check_if_address_the_same'
                                    } }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.CHECKBOX,
                                        name: 'mailingAddressSameAsHomeAddressIndicator',
                                        classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02' ],
                                        checked: mailingAddressSameAsHomeAddressIndicator,
                                        value: mailingAddressSameAsHomeAddressIndicator,
                                        currentData: primaryContact,
                                        fields: [ 'householdContact' ],
                                        isInputStandalone: true,
                                        disabled: noHomeAddressIndicator
                                    }}
                                    inputActions = { {
                                        onFieldChange: onCheckTheSameAddress
                                    } }
                                />
                            </div>
                        </fieldset>

                        {
                            !mailingAddressSameAsHomeAddressIndicator &&

                            <MailingAddressInputs
                                {...{
                                    _postalCode,
                                    _countyCode,
                                    _streetAddress1,
                                    _streetAddress2,
                                    _state,
                                    _city,
                                    currentData: primaryContact,
                                    counties: fetchedCounties(MAILING_ADDRESS),
                                    inputActions: {
                                        onFieldChange
                                    },
                                    underFormControl: true,
                                    isInputStandalone: true
                                }}
                            />
                        }
                    </div>
                    <div className = 'subsection' >
                        <PageHeader
                            text = 'ssap.subheader.primary_contact_phone'
                            headerClassName = 'gi-heading-alt h4-not-highlighted'
                        />

                        <div className="grid-row gi-component gi-regular_input align-items-end">
                            <label
                                htmlFor='ssap.input.label.mobile_phone'
                                className='tablet:grid-col-4 gi-regular_input__label iconed-false'
                            >
                                <FormattedMessage
                                    id = 'ssap.input.label.mobile_phone'
                                    values = { { alt: isState.ID } }
                                />
                            </label>

                            <div className='grid-col-6' >
                                <UniversalInput
                                    errorClasses = { [ 'm-0', 'mb-4' ] }
                                    aria-labelledby = { 'ssap.input.label.mobile_phone' }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.DUMMY,
                                        name: 'phoneNumber',
                                        value: phoneNumber,
                                        validationType: VALIDATION_TYPES.PHONE_NUMBER_PROVIDED,
                                        isInputStandalone: true,
                                        renderAs: (
                                            <div className = 'grid-row' >
                                                {
                                                    phoneNumber
                                                        ? (
                                                            <div
                                                                className = { 'tablet:grid-col-6 txt-left margin-top-2' }
                                                                data-test-id = 'preferences_phone_number'
                                                            >
                                                                { phoneNumberTools.getAdjustedPhoneNumber(phoneNumber) }
                                                            </div>
                                                        )
                                                        : null
                                                }
                                                <div className = 'tablet:grid-col-6' >
                                                    <div className = { `grid-row ${ phoneNumber ? 'float-right' : 'float-left' }` } >
                                                        <Button
                                                            id = 'preferences_phone_number_button'
                                                            dataTestId = 'preferences_phone_number_button'
                                                            name = 'phoneNumber'
                                                            classes = { [ 'usa-button', 'gi-button__outline' ] }
                                                            type = { BUTTON_INPUT_TYPES.BUTTON }
                                                            btnText = { `ssap.button.${ phoneNumber ? 'change_phone' : 'add_phone' }`}
                                                            icon = 'edit'
                                                            iconPlacement = 'START'
                                                            handleClick = { handleChangePhoneModal }
                                                            renderWithIconOnly = { !!phoneNumber }
                                                            outerClass = { [ 'tablet:grid-col-auto' ] }
                                                        />
                                                        {
                                                            phoneNumber
                                                                ? (
                                                                    <Button
                                                                        id = 'preferences_delete_phone_number_button'
                                                                        dataTestId = 'preferences_delete_phone_number_button'
                                                                        name = 'deletePhoneNumber'
                                                                        classes = { [ 'usa-button', 'gi-button__outline', 'margin-left-2' ] }
                                                                        type = { BUTTON_INPUT_TYPES.BUTTON }
                                                                        btnText = 'ssap.button.delete_phone'
                                                                        icon = 'trash'
                                                                        iconPlacement = 'START'
                                                                        handleClick = { handleDeleteMobilePhoneNumber }
                                                                        outerClass = { ['tablet:grid-col-auto'] }
                                                                        renderWithIconOnly
                                                                    />
                                                                )
                                                                : null
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }}
                                />
                            </div>
                        </div>

                        { 
                            (phoneNumber && (phoneVerified && smsVerified && !smsUnsubscribed))
                                ? (
                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.text_me',
                                            replacements: {
                                                privacyPolicy: (
                                                    <FormattedMessage
                                                        id="ssap.input.label.privacy_policy"
                                                        values={{
                                                            link: (
                                                                <a
                                                                    className="usa-link--external"
                                                                    href={PRIVACY_POLICY}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <FormattedMessage id="ssap.start.primary_contact_info.link_text" />
                                                                </a>
                                                            ),
                                                        }}
                                                    />
                                                )
                                            }
                                        } }
                                        inputData = { {
                                            isInputRequired: false,
                                            type: INPUT_TYPES.CHECKBOX,
                                            name: 'textMe',
                                            classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02' ],
                                            checked: textMe,
                                            value: textMe,
                                            currentData: primaryContact,
                                            fields: [ 'householdContact', 'contactPreferences' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange
                                        } }
                                    />
                                )
                                : null
                        }

                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.home_phone',
                                replacements: { alt: isState.ID },
                                labelClasses: []
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                                name: '_phoneNumber',
                                helperClasses: [],
                                value: _phoneNumber,
                                placeholder: 'ssap.input.placeholder.phone_number',
                                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'otherPhone' ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange: handlePhoneChange
                            } }
                        />
                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.phone_extension',
                                labelClasses: []
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.REGULAR_INPUT,
                                name: 'phoneExtension',
                                helperClasses: [],
                                value: phoneExtension,
                                placeholder: 'ssap.input.placeholder.phone_extension',
                                validationType: VALIDATION_TYPES.PHONE_EXTENSION,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'otherPhone' ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                        />
                    </div>

                    {
                        isState.VA &&

                        (
                            <div className = 'subsection' >
                                <EmergencyContact
                                    isNameWarningShown = { isNameWarningShown }
                                    underFormControl
                                    isInputStandalone
                                />
                            </div>
                        )
                    }

                    <div className = 'subsection' >
                        <PageHeader
                            text = 'ssap.subheader.primary_contact_preferences'
                            headerClassName = 'gi-heading-alt'
                        />
                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.preferred_spoken_language'
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.DROPDOWN,
                                name: 'preferredSpokenLanguage',
                                value: preferredSpokenLanguage,
                                options: DROPDOWN_OPTIONS.LANGUAGES_FILTERED('spoken', stateCode),
                                valuesToMatch: DROPDOWN_OPTIONS.LANGUAGES_FILTERED('spoken', stateCode).map(lang => lang.value),
                                validationType: VALIDATION_TYPES.CHOSEN_FROM_LIST,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'contactPreferences' ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                        />
                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.preferred_written_language'
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.DROPDOWN,
                                name: 'preferredWrittenLanguage',
                                value: preferredWrittenLanguage,
                                options: DROPDOWN_OPTIONS.LANGUAGES_FILTERED('written', stateCode),
                                valuesToMatch: DROPDOWN_OPTIONS.LANGUAGES_FILTERED('written', stateCode).map(lang => lang.value),
                                validationType: VALIDATION_TYPES.CHOSEN_FROM_LIST,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'contactPreferences' ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                        />

                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && !contactMethodComplete }
                            messageText = { VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL }
                            errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                        />

                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.communication_method_notices'
                            } }
                            label = { {
                                labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'preferredContactMethod',
                                value: preferredContactMethod,
                                options: DROPDOWN_OPTIONS.DELIVERY_OPTIONS_NOTICES,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'contactPreferences' ],
                                isInputStandalone: true
                            } }
                            errorClasses = { [ 'margin-l-0' ] }
                            inputActions = { {
                                onFieldChange
                            } }
                        />
                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.communication_method_1095',
                                dataInstructions: 'instructional_text.communication_method_1095',
                                instructionsViaPortal: [ true, '7rem' ],
                                replacements: { exchangeName }
                            } }
                            label = { {
                                labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'paperless1095',
                                value: paperless1095,
                                options: DROPDOWN_OPTIONS.DELIVERY_OPTIONS_1095,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: primaryContact,
                                fields: [ 'householdContact', 'contactPreferences' ],
                                isInputStandalone: true
                            } }
                            errorClasses = { [ 'margin-l-0' ] }
                            inputActions = { {
                                onFieldChange
                            } }
                        />
                        <div className = 'grid-row' >
                            <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                                <div className = 'usa-alert__body gi-alert__info__body' >
                                    <p className = 'usa-alert__text gi-alert__info__text' >
                                        <FormattedMessage id = 'ssap.start.primary_contact_info.notifications_warning' />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    counties: state.ssap.counties,
    data: state.ssap.data,
    form: state.ssap.form
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        showModal,
        changeData,
        fetchCommunicationPreferences,
        fetchCountiesForZipcode,
        setCounty,
        validateAddress
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page1_02);
